import { EXPORT_BASE, FETCH_EXPORTS, GENERATE_EXPORT_TOKEN } from "@apiUtils";
import { mutationFetch, queryFetch } from "@atlas/sdk";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const HUB_EXPORTS_KEY = "hubExportsKey";
export const HUB_EXPORT_TOKEN_KEY = "hubExportTokenKey";

export const getHubExportsKey = () => [HUB_EXPORTS_KEY] as const;

export const getHubExportTokenKey = (exportId: string) =>
  [HUB_EXPORT_TOKEN_KEY, exportId] as const;

export type ExportHubCatalogsPayload = {
  catalogs: { catalogId: number; version: number }[];
};

const exportHubCatalogs = (
  body: ExportHubCatalogsPayload,
): Promise<HubExportResponse> => {
  return mutationFetch<HubExportResponse>({
    url: EXPORT_BASE,
    method: "POST",
    body,
  });
};

export const useExportHubCatalogs = (
  options?: UseMutationOptions<
    HubExportResponse,
    AxiosError,
    ExportHubCatalogsPayload
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exportHubCatalogs,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getHubExportsKey() });
      options?.onSuccess?.(...args);
    },
  });
};

export type ExportHubPackagesPayload = {
  packages?: number[];
  catalogs?: { id: number; version: number }[];
};

export interface HubExportResponse {
  data: string;
  message: string;
  success: boolean;
}

const exportHubPackages = (
  body: ExportHubPackagesPayload,
): Promise<HubExportResponse> => {
  return mutationFetch<HubExportResponse>({
    url: EXPORT_BASE,
    method: "POST",
    body,
  });
};

export const useExportHubPackages = (
  options?: UseMutationOptions<
    HubExportResponse,
    AxiosError,
    ExportHubPackagesPayload
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exportHubPackages,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getHubExportsKey() });
      options?.onSuccess?.(...args);
    },
  });
};

export interface IHubExport {
  exportId: string;
  exportFile: string;
  exportFeature: string;
  featureConfig: {
    Features: string;
  };
  createdBy: string;
  createdDatetime: string;
}

const fetchHubExports = (): Promise<IHubExport[]> => {
  return queryFetch<IHubExport[]>({
    url: FETCH_EXPORTS,
  });
};

export const useHubExports = (
  options?: Omit<
    UseQueryOptions<
      IHubExport[],
      AxiosError,
      IHubExport[],
      ReturnType<typeof getHubExportsKey>
    >,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: getHubExportsKey(),
    queryFn: fetchHubExports,
    ...options,
  });
};

const generateHubExportToken = (exportId: string): Promise<string> => {
  const url = GENERATE_EXPORT_TOKEN.replace(":exportId", exportId);
  return queryFetch<string>({
    url,
  });
};

export const useGenerateHubExportToken = (
  exportId: string,
  options?: Omit<
    UseQueryOptions<
      string,
      AxiosError,
      string,
      ReturnType<typeof getHubExportTokenKey>
    >,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: getHubExportTokenKey(exportId),
    queryFn: () => generateHubExportToken(exportId),
    ...options,
  });
};
